import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setToken, getToken, clearImpersonation } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // No stored token means no session: every sign-in path stores a bearer
    // token, so probing /auth/me here only produced a 401 on every public
    // page load (ticket 98 N11).
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from a Google OAuth callback (the `code` query
    // param), skip the /me check — AuthCallback establishes the session first.
    const isOAuthCallback = window.location.pathname === '/portal/auth/callback'
      && window.location.search.includes('code=');
    if (isOAuthCallback) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Every fresh session token ends any login-as state left behind by an
  // earlier session (ticket 73), so the impersonation banner never shows for
  // a user who signed in normally.
  const issueToken = (t) => { clearImpersonation(); setToken(t); };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { data } = response;
    if (response.status === 202) {
      return { mfaRequired: true, mfaToken: data.mfa_token, enrolled: data.enrolled };
    }
    issueToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const completeMfa = ({ access_token, user }) => {
    issueToken(access_token);
    setUser(user);
    return user;
  };

  const completeGoogleLogin = ({ access_token, user }) => {
    issueToken(access_token);
    setUser(user);
    return user;
  };

  // Ticket 166: signup never logs in. It emails a verification link, and the
  // account (password included) is created when that link is clicked —
  // VerifyEmailPage. Same shape for the ticket-115 borrower-seat claim.
  const signup = async (payload) => {
    const { data } = await api.post('/auth/signup', payload);
    return { verificationSent: true, email: data.email };
  };

  // Ticket 59: one POST validates the seat and rewrites the active tenant; the
  // JWT does not change. The response is the new /auth/me shape.
  const switchTenant = async (tenantId) => {
    const { data } = await api.post('/tenants/switch', { tenant_id: tenantId });
    setUser(data);
    return data;
  };

  // Fold fields the user just saved about themself into the signed-in user,
  // without the loading flash a /auth/me refetch causes.
  const updateUser = useCallback((patch) => setUser((u) => (u ? { ...u, ...patch } : u)), []);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setToken(null);
    clearImpersonation();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, completeMfa, completeGoogleLogin, signup, logout, checkAuth, switchTenant, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
